/**
 * CaptionGeneratorModal — genera 3 caption Instagram pronte da copiare.
 *
 * UX:
 *  - Step 1: l'agente sceglie il "tipo di contenuto" (nuovo annuncio, venduto,
 *    stima, restyling, arreda, wrapped, open house, custom).
 *  - Step 2: campi opzionali in base al tipo (città, prezzo, zona, ecc.).
 *  - Step 3: click "Genera 3 caption" → backend Claude Sonnet 4.5 → ritorna 3
 *    varianti (editoriale, diretta, storytelling).
 *  - Step 4: l'agente vede le 3 caption in cards swipeable + tap "Copia".
 *
 * Endpoint: POST /api/social/captions
 * Quota:    GET  /api/social/captions/quota
 */
import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import { X, Copy, Sparkle, ArrowsClockwise } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { useBgJobs } from "@/components/jobs/useBgJobs";

const CONTEXTS = [
  { v: "nuovo_annuncio", l: "Nuovo annuncio", emoji: "🏠", desc: "Hai appena pubblicato un immobile" },
  { v: "venduto",        l: "Venduto",        emoji: "🔑", desc: "Rogito firmato, una vendita chiusa" },
  { v: "stima",          l: "Stima casa",     emoji: "📊", desc: "Promuovi il tuo strumento di stima" },
  { v: "restyling",      l: "Restyling",   emoji: "✨", desc: "Prima/dopo di una stanza ristrutturata" },
  { v: "arreda",         l: "Spazio arredato", emoji: "🛋️", desc: "Virtual staging di uno spazio vuoto" },
  { v: "wrapped",        l: "Report annuale", emoji: "🏆", desc: "Il tuo Wrapped — bilancio dell'anno" },
  { v: "open_house",     l: "Open house",     emoji: "📅", desc: "Visita aperta in programma" },
  { v: "custom",         l: "Altro",          emoji: "✍️", desc: "Contenuto generico immobiliare" },
];

// Campi dinamici per contesto: chiave + label + placeholder
const FIELD_PRESETS = {
  nuovo_annuncio: [
    { k: "citta",         l: "Città",          ph: "Milano" },
    { k: "zona",          l: "Zona / Quartiere", ph: "Brera" },
    { k: "tipologia",     l: "Tipologia",      ph: "Trilocale" },
    { k: "prezzo",        l: "Prezzo",         ph: "€ 450.000" },
    { k: "superficie",    l: "Superficie",     ph: "95 m²" },
    { k: "caratteristiche", l: "Caratteristiche", ph: "Balcone, ascensore, terzo piano", wide: true },
  ],
  venduto: [
    { k: "citta",  l: "Città",       ph: "Milano" },
    { k: "tipologia", l: "Tipologia", ph: "Bilocale" },
    { k: "giorni_vendita", l: "Giorni di vendita (opzionale)", ph: "32" },
  ],
  stima: [
    { k: "citta",  l: "Città / Zona di lavoro", ph: "Milano · Brera" },
    { k: "punto_forza", l: "Tuo punto di forza", ph: "Dati ufficiali OMI, gratuita, in 30 secondi", wide: true },
  ],
  restyling: [
    { k: "stanza", l: "Stanza", ph: "Soggiorno" },
    { k: "stile",  l: "Stile",  ph: "Moderno scandinavo" },
  ],
  arreda: [
    { k: "stanza", l: "Stanza", ph: "Camera" },
    { k: "stile",  l: "Stile",  ph: "Minimal contemporaneo" },
  ],
  wrapped: [
    { k: "anno",            l: "Anno",            ph: "2026" },
    { k: "case_vendute",    l: "Case vendute",    ph: "12" },
    { k: "volume_transato", l: "Volume transato", ph: "€ 3.8M" },
  ],
  open_house: [
    { k: "data",  l: "Data",   ph: "Sabato 24 maggio, 15:00-18:00" },
    { k: "citta", l: "Città",  ph: "Milano" },
    { k: "indirizzo_zona", l: "Zona", ph: "Brera" },
  ],
  custom: [
    { k: "argomento", l: "Argomento", ph: "Es. Consigli per chi cerca casa a Milano", wide: true },
  ],
};

export default function CaptionGeneratorModal({ open, onClose, initialContext, initialDetails }) {
  const [context, setContext] = useState(initialContext || null);
  const [details, setDetails] = useState(initialDetails || {});
  const [busy, setBusy] = useState(false);
  const [captions, setCaptions] = useState(null);
  const [copiedIdx, setCopiedIdx] = useState(null);
  const bgJobs = useBgJobs();

  useEffect(() => {
    if (open) {
      setContext(initialContext || null);
      setDetails(initialDetails || {});
      setCaptions(null);
    }
  }, [open, initialContext, initialDetails]);

  const fields = context ? (FIELD_PRESETS[context] || []) : [];

  const submit = async () => {
    if (!context) return;
    setBusy(true);
    // iter v31 r13 — Tray globale: caption Instagram in lavorazione.
    const _bgHandle = bgJobs.start("caption_social");
    try {
      const { data } = await api.post("/social/captions", {
        context_type: context,
        details,
      });
      setCaptions(data.captions || []);
      _bgHandle.end(true);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Errore generazione caption";
      const friendly = typeof msg === "string" ? msg : "Errore generazione caption";
      _bgHandle.end(false, friendly);
      toast.error(friendly);
    } finally {
      setBusy(false);
    }
  };

  const copy = async (text, idx) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedIdx(idx);
      setTimeout(() => setCopiedIdx(null), 1500);
      toast.success("Caption copiata · incollala su Instagram");
    } catch {
      toast.error("Copia fallita");
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/55 backdrop-blur-sm flex items-end sm:items-center justify-center p-3 sm:p-6"
      onClick={() => !busy && onClose()}
      data-testid="caption-generator-modal"
    >
      <div
        className="bg-white rounded-2xl w-full max-w-[640px] max-h-[92vh] overflow-y-auto shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-white z-10 px-5 sm:px-6 pt-5 pb-3 border-b border-[#F0EFEA] flex items-start justify-between gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#C8A66B] mb-1 inline-flex items-center gap-1.5">
              <Sparkle size={11} weight="fill" /> Caption · Instagram
            </div>
            <h2 className="text-[18px] font-semibold text-[#1C1C1A] leading-tight">
              {captions ? "Le tue 3 caption sono pronte" : "Cosa vuoi pubblicare?"}
            </h2>
            <p className="text-[12px] text-[#6B6A65] mt-1">
              {captions
                ? "Copia quella che ti piace di più, incollala su Instagram."
                : "Pronto genera 3 varianti con stili diversi. Scegli il tono, poi copia."}
            </p>
          </div>
          <button onClick={() => !busy && onClose()} className="text-[#9A968B] hover:text-[#1C1C1A] p-1" aria-label="Chiudi" data-testid="caption-close">
            <X size={18} />
          </button>
        </div>

        <div className="p-5 sm:p-6 space-y-5">
          {!captions && (
            <>
              {/* Context picker */}
              <div data-testid="caption-context-grid">
                <div className="text-[10px] uppercase tracking-[0.16em] font-bold text-[#6B6A65] mb-2">Tipo di contenuto</div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5">
                  {CONTEXTS.map((c) => (
                    <button
                      key={c.v}
                      onClick={() => setContext(c.v)}
                      className={`text-left p-2.5 rounded-lg border transition-all ${
                        context === c.v
                          ? "border-[#7C8B6E] bg-[#F1F4ED]"
                          : "border-[#EAE7DF] bg-white hover:border-[#7C8B6E]"
                      }`}
                      data-testid={`caption-ctx-${c.v}`}
                      aria-pressed={context === c.v}
                    >
                      <div className="text-[16px] mb-0.5">{c.emoji}</div>
                      <div className="text-[12px] font-semibold text-[#1C1C1A] leading-tight">{c.l}</div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Dynamic fields */}
              {context && fields.length > 0 && (
                <div data-testid="caption-fields">
                  <div className="text-[10px] uppercase tracking-[0.16em] font-bold text-[#6B6A65] mb-2">
                    Dettagli (tutti opzionali) — più dai a Pronto, migliori le caption
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {fields.map((f) => (
                      <div key={f.k} className={f.wide ? "sm:col-span-2" : ""}>
                        <label className="block text-[10.5px] uppercase tracking-[0.12em] font-semibold text-[#9A968B] mb-1">{f.l}</label>
                        <input
                          type="text"
                          value={details[f.k] || ""}
                          onChange={(e) => setDetails({ ...details, [f.k]: e.target.value })}
                          placeholder={f.ph}
                          className="w-full px-3 py-2 bg-white border border-[#E5E7E0] rounded text-[13.5px] focus:outline-none focus:border-[#7C8B6E]"
                          data-testid={`caption-field-${f.k}`}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <button
                onClick={submit}
                disabled={!context || busy}
                className="w-full py-3 rounded-full bg-[#7C8B6E] hover:bg-[#6A7860] text-white text-[13.5px] font-semibold disabled:opacity-50 transition-colors inline-flex items-center justify-center gap-2"
                data-testid="caption-generate-btn"
              >
                <Sparkle size={14} weight="fill" /> {busy ? "Sto scrivendo le caption…" : "Genera 3 caption"}
              </button>
              <p className="text-[10.5px] text-[#9A968B] text-center -mt-2 italic">
                Genera in 4 secondi · gratuita
              </p>
            </>
          )}

          {captions && (
            <>
              <div className="space-y-3" data-testid="caption-results">
                {captions.map((c, i) => (
                  <CaptionCard
                    key={i}
                    variant={c.variant}
                    text={c.text}
                    hashtags={c.hashtags}
                    onCopy={() => copy([c.text, c.hashtags].filter(Boolean).join("\n\n"), i)}
                    copied={copiedIdx === i}
                  />
                ))}
              </div>
              <div className="flex flex-col sm:flex-row gap-2">
                <button
                  onClick={() => { setCaptions(null); }}
                  className="flex-1 py-2.5 rounded-full bg-white border border-[#1C1C1A] text-[#1C1C1A] text-[13px] font-semibold hover:bg-[#F1F4ED] transition-colors inline-flex items-center justify-center gap-2"
                  data-testid="caption-restart"
                >
                  ← Cambia tipo di contenuto
                </button>
                <button
                  onClick={submit}
                  disabled={busy}
                  className="flex-1 py-2.5 rounded-full bg-[#7C8B6E] hover:bg-[#6A7860] text-white text-[13px] font-semibold disabled:opacity-50 transition-colors inline-flex items-center justify-center gap-2"
                  data-testid="caption-regenerate"
                >
                  <ArrowsClockwise size={13} weight="bold" /> Genera altre 3 varianti
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

const VARIANT_LABELS = {
  editoriale:   { l: "Editoriale",   sub: "Pulita, da rivista", color: "#7C8B6E" },
  diretta:      { l: "Diretta",      sub: "Corta e punchy",     color: "#C24E4E" },
  storytelling: { l: "Storytelling", sub: "Personale, dal cuore", color: "#C8A66B" },
  raw:          { l: "Caption",      sub: "",                   color: "#1C1C1A" },
};

function CaptionCard({ variant, text, hashtags, onCopy, copied }) {
  const meta = VARIANT_LABELS[variant] || VARIANT_LABELS.raw;
  return (
    <div className="bg-[#FAF9F5] border border-[#EAE7DF] rounded-xl overflow-hidden">
      <div className="flex items-center justify-between gap-3 px-4 py-2.5 border-b border-[#EAE7DF] bg-white">
        <div className="flex items-center gap-2">
          <span className="w-1.5 h-1.5 rounded-full" style={{ background: meta.color }} />
          <span className="text-[12px] font-semibold text-[#1C1C1A] uppercase tracking-[0.14em]">{meta.l}</span>
          {meta.sub && <span className="text-[11px] text-[#9A968B]">· {meta.sub}</span>}
        </div>
        <button
          onClick={onCopy}
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11.5px] font-semibold transition-all ${
            copied ? "bg-[#7C8B6E] text-white" : "bg-[#1C1C1A] text-white hover:bg-[#3F3D38]"
          }`}
          data-testid={`caption-copy-${variant}`}
        >
          <Copy size={12} weight={copied ? "fill" : "bold"} />
          {copied ? "Copiata!" : "Copia"}
        </button>
      </div>
      <div className="px-4 py-3">
        <p className="text-[13.5px] text-[#1C1C1A] leading-relaxed whitespace-pre-wrap">{text}</p>
        {hashtags && (
          <p className="text-[12px] text-[#7C8B6E] font-medium mt-2 leading-relaxed break-words">
            {hashtags}
          </p>
        )}
      </div>
    </div>
  );
}
